import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import * as filesize from 'filesize';
import { Map } from 'immutable';
import { isEqual } from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';

import { Editor, EditorApi, MentionItem, validators } from 'react-text-editor';

import { NoteAttachment, NoteView } from 'shared/models/note';
import { UserData } from 'shared/models/user';
import { FilePayload, HrefFilePayload, S3FilePayload } from 'shared/types/file-payload';

import { addNoteAttachments, createNewNote, getConfirmation, removeNoteAttachments } from '../actions';
import { updateDraft } from '../common/draft-storage';
import { logger } from '../common/logger';
import { withUsers } from '../hocs/with-users';
import { readFile } from '../lib/read-file-payload';
import { Avatar } from '../sfc/avatar';
import { NoteDraftState, State } from '../state';
import { Attachment } from './attachment';
import { AttachmentButton } from './attachment-button';

const avatarSize = 32;
const spinnerSize = 20;
const spinnerThickness = 2;
const maxAttachmentSize = 26214400;
const defaultNoteFormat: 'html' | 'markdown' = 'markdown';

interface OwnProps {
    initialDraft: NoteDraftState;
    context: { [field: string]: any };
    format?: 'html' | 'markdown';
    noteDraftKey: string;
    notable: string;
    highlightErrors: boolean;
    hasChanged: (content: string) => boolean;
    id?: string;
    placeholder?: string;
    postDiscard?: () => void;
    postSave?: () => void;
    parentId?: string;
}

interface ConnectedProps {
    noteAttachments: Map<string, FilePayload[]>;
    user: UserData;
}

interface ConnectedDispatch {
    addNoteAttachments: (noteDraftKey: string, attachments: FilePayload[]) => void;
    removeNoteAttachments: (noteDraftKey: string) => void;
    getConfirmation: (onConfirm: () => void, description?: string, title?: string) => void;
    createNewNote: (note: Partial<NoteView>, postSave?: () => void, onFailedSave?: () => void) => void;
}

interface ConnectedPropsUsers {
    users: Map<string, UserData>;
}

type NoteEditorComponentProps = ConnectedProps & ConnectedDispatch & ConnectedPropsUsers & OwnProps;

interface NoteEditorComponentState {
    errorsHighlighted: boolean;
    initialAttachments: FilePayload[];
    draft: NoteDraftState;
    mentions: MentionItem[];
}

const confirmMessage = 'Note not saved, discard changes?';

class NoteEditorComponent extends React.Component<NoteEditorComponentProps, NoteEditorComponentState> {
    private editorRef = React.createRef<EditorApi>();

    constructor(props: NoteEditorComponentProps) {
        super(props);
        const { highlightErrors, initialDraft, noteAttachments, noteDraftKey } = props;
        const draft = initialDraft;
        const initialAttachments: FilePayload[] =
            draft.initialAttachments.map((a) => ({ ...a, type: 's3Key' as 's3Key' })) ?? [];
        if (!noteAttachments.get(noteDraftKey)) {
            props.addNoteAttachments(noteDraftKey, initialAttachments);
        }

        this.state = {
            draft,
            errorsHighlighted: highlightErrors,
            initialAttachments,
            mentions: []
        };
    }

    getNoteFormat = (context: { [field: string]: any }): 'html' | 'markdown' => {
        if (this.props.format) {
            return this.props.format;
        }

        if (context?.isSubmissionNote === true) {
            return 'html';
        }

        return defaultNoteFormat;
    };

    componentDidMount() {
        if (this.props.highlightErrors && this.editorRef.current) {
            this.editorRef.current.validate();
        }
    }

    handleUpdateNoteDraft = (updates: Partial<NoteDraftState>) => {
        const newDraft = { ...this.state.draft, ...updates, modifiedAt: Date.now() };
        updateDraft(this.props.noteDraftKey, newDraft);
        this.setState({ draft: newDraft });
    };

    handleFailedSave = () => {
        this.handleUpdateNoteDraft({ saving: false });
    };

    handleSaveNote = () => {
        const { notable, noteDraftKey, user, noteAttachments, parentId } = this.props;
        const { draft } = this.state;
        const draftAttachments = noteAttachments.get(noteDraftKey) || [];
        const { content } = draft;
        if (!this.noteHasErrors()) {
            const context = { ...draft.context, ...this.props.context };
            const noteFormat = this.getNoteFormat(context);
            const userId = user.id;
            const savedAttachments: NoteAttachment[] = (
                draftAttachments.filter((a) => a.type === 's3Key') as S3FilePayload[]
            ).map((a) => ({ filename: a.filename, key: a.key, size: a.size }));
            const newAttachments: HrefFilePayload[] = draftAttachments.filter(
                (a) => a.type === 'href'
            ) as HrefFilePayload[];
            const mentionedUserIds = this.state.mentions.map((m) => m.id as string);
            const newNote: Partial<NoteView> = {
                attachments: savedAttachments,
                content,
                context,
                format: noteFormat,
                mentionedUserIds,
                modifiedBy: userId,
                newAttachments,
                noAIAccess: draft.noAIAccess,
                notable,
                parentId
            };
            if (this.props.id) {
                newNote.id = this.props.id;
            } else {
                newNote.createdBy = userId;
            }
            this.handleUpdateNoteDraft({ saving: true });
            this.props.createNewNote(newNote, this.props.postSave, this.handleFailedSave);
            this.props.removeNoteAttachments(this.props.noteDraftKey);
        } else {
            this.props.getConfirmation(
                null,
                'Please fill in the variables marked by curly {} braces in the body',
                'Errors'
            );
            this.setState({ errorsHighlighted: true });
        }
    };

    onChange = (content: string) => {
        this.handleUpdateNoteDraft({ content });
    };

    handleEditorContentChange = (data: { value: string; tags: MentionItem[] }) => {
        this.handleUpdateNoteDraft({ content: data.value });
        this.setState({ mentions: data.tags });
    };

    handleConfirmedDiscard = () => {
        this.props.removeNoteAttachments(this.props.noteDraftKey);
        if (this.props.postDiscard) {
            this.props.postDiscard();
        }
    };

    handleDiscard = () => {
        const { draft } = this.state;
        const content = draft ? draft.content : '';
        if (!this.props.hasChanged(content) && !this.attachmentsHasChanged()) {
            this.handleConfirmedDiscard();
        } else {
            this.props.getConfirmation(this.handleConfirmedDiscard, confirmMessage, 'Confirm');
        }
    };

    attachmentUnderSizeLimit = (newFiles: Array<{ size: number }>) => {
        const { noteAttachments, noteDraftKey } = this.props;
        const attachments = noteAttachments.get(noteDraftKey);
        let errorString;
        let totalSize = attachments.map((a) => a.size).reduce((acc, s) => s + acc, 0);
        for (const file of newFiles) {
            totalSize += file.size;
            if (file.size > maxAttachmentSize) {
                errorString = `Maximum size for an attachment is ${filesize(maxAttachmentSize, { round: 0 })}`;
            }
        }
        if (!errorString && totalSize > maxAttachmentSize) {
            errorString = `Maximum total size for attachments is ${filesize(maxAttachmentSize, { round: 0 })}`;
        }
        if (errorString) {
            this.props.getConfirmation(null, errorString, 'Errors');
            return false;
        } else {
            return true;
        }
    };

    handleAddPreloadedFiles = (files: S3FilePayload[]) => {
        if (this.attachmentUnderSizeLimit(files)) {
            const { noteAttachments, noteDraftKey } = this.props;
            const attachments = noteAttachments.get(noteDraftKey);
            const newAttachments = attachments.concat(files);
            this.props.addNoteAttachments(noteDraftKey, newAttachments);
        }
    };

    handleAddAttachments = (files: File[]) => {
        if (this.attachmentUnderSizeLimit(files)) {
            const { noteAttachments, noteDraftKey } = this.props;
            const attachments = noteAttachments.get(noteDraftKey);
            this.handleUpdateNoteDraft({ addingAttachment: true });
            Promise.all(files.map(readFile))
                .then((result) => {
                    const newAttachments = attachments.concat(result);
                    this.props.addNoteAttachments(noteDraftKey, newAttachments);
                    this.handleUpdateNoteDraft({ addingAttachment: false });
                })
                .catch((err) => {
                    logger.warn(err, { files, message: 'error adding attachments to note' });
                    this.props.getConfirmation(null, 'There was a problem adding the attachment', 'Errors');
                    this.props.addNoteAttachments(noteDraftKey, attachments);
                    this.handleUpdateNoteDraft({ addingAttachment: false });
                });
        }
    };

    handleRemoveAttachment = (index: number) => () => {
        const attachments = this.props.noteAttachments.get(this.props.noteDraftKey);
        const newAttachments = attachments.slice(0, index).concat(attachments.slice(index + 1));
        this.props.addNoteAttachments(this.props.noteDraftKey, newAttachments);
    };

    handleAttachmentFileNameUpdate = (index: number) => (filename: string) => {
        const attachments = this.props.noteAttachments.get(this.props.noteDraftKey);
        const newAttachments = attachments.map((a, i) => {
            if (i === index) {
                return {
                    ...a,
                    filename
                };
            } else {
                return a;
            }
        });
        this.props.addNoteAttachments(this.props.noteDraftKey, newAttachments);
    };

    noteHasErrors = () => {
        const { highlightErrors } = this.props;
        if (highlightErrors && this.editorRef.current) {
            const errors = this.editorRef.current.validate();
            return errors.errorCount > 0;
        }
        return false;
    };

    handleToggleErrorHighlights = () => {
        if (this.editorRef.current) {
            if (this.state.errorsHighlighted) {
                this.editorRef.current.clearValidation();
            } else {
                this.editorRef.current.validate();
            }
            this.setState({ errorsHighlighted: !this.state.errorsHighlighted });
        }
    };

    handleToggleAIAccess = () => {
        this.handleUpdateNoteDraft({ noAIAccess: !this.state.draft.noAIAccess });
    };

    attachmentsHasChanged = () => {
        return !isEqual(this.state.initialAttachments, this.props.noteAttachments.get(this.props.noteDraftKey));
    };

    render() {
        const { user, noteDraftKey, highlightErrors, placeholder, noteAttachments, users } = this.props;
        const attachments = noteAttachments.get(noteDraftKey) || [];
        const { errorsHighlighted, draft } = this.state;

        if (!draft) {
            return (
                <div className="note-editor-container">
                    <div className="note-header note-form flex-columns-container">
                        <Avatar entity={user} size={avatarSize} />
                    </div>
                </div>
            );
        }

        const { content, saving, addingAttachment } = draft;
        const editorFormat = this.getNoteFormat({ ...draft.context, ...this.props.context });

        let attachmentsList;
        if (attachments.length > 0) {
            attachmentsList = attachments.map((f, i) => (
                <Attachment
                    key={i}
                    file={f}
                    onRemove={this.handleRemoveAttachment(i)}
                    onFileNameUpdate={this.handleAttachmentFileNameUpdate(i)}
                />
            ));
        }
        const attachmentsSection =
            attachments.length > 0 ? <div className="note-editor-attachments">{attachmentsList}</div> : null;

        const editorMentionOptions = highlightErrors
            ? []
            : users
                  .filter((u) => u.status === 'active')
                  .valueSeq()
                  .map((u) => ({ label: u.name.full, id: u.id }))
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .concat([{ label: 'here', id: '@here' }])
                  .toArray();

        const form = (
            <div className="note-form-content-rte">
                <Editor
                    ref={this.editorRef}
                    defaultValue={content}
                    autoFocus={true}
                    onChange={this.handleEditorContentChange}
                    mentionOptions={editorMentionOptions}
                    disabled={saving}
                    format={editorFormat}
                    validateContent={validators.braces}
                    placeholder={placeholder}
                >
                    {attachmentsSection}
                </Editor>
            </div>
        );

        const discardButton = saving ? null : <Button onClick={this.handleDiscard}>Discard</Button>;
        const spinner = saving ? <CircularProgress size={spinnerSize} thickness={spinnerThickness} /> : null;
        const savedDisabled =
            saving || (!this.props.hasChanged(content) && !this.attachmentsHasChanged()) || addingAttachment;
        const saveButton = (
            <Button onClick={this.handleSaveNote} disabled={savedDisabled}>
                Save
            </Button>
        );

        const notableParts = this.props.notable.split('-');
        const personId = notableParts[0] === 'persons' ? notableParts[1] : null;
        const addAttachmentsButton = (
            <AttachmentButton
                personId={personId}
                jobId={this.props.context.jobId}
                onDrop={this.handleAddAttachments}
                onUploadedFilesSelect={this.handleAddPreloadedFiles}
            />
        );
        const aiAccessTooltip = this.state.draft.noAIAccess ? 'AI Access Disabled' : 'AI Access Enabled';
        const aiAccessButton = this.props.notable.startsWith('clients') ? (
            <Tooltip title={aiAccessTooltip}>
                <div className="email-compose-attach-action" onClick={this.handleToggleAIAccess}>
                    <button className="icon-label">
                        <i className={this.state.draft.noAIAccess ? 'fas fa-eye-slash' : 'fas fa-eye'} />
                    </button>
                </div>
            </Tooltip>
        ) : null;

        const toggleHighlight = highlightErrors ? (
            <Tooltip title="Toggle Error Highlights">
                <button
                    className={`icon-label ${errorsHighlighted ? 'button-active' : ''}`}
                    onClick={this.handleToggleErrorHighlights}
                    disabled={saving}
                >
                    <i className="material-icons">border_color</i>
                </button>
            </Tooltip>
        ) : null;

        return (
            <div className="note-editor-container">
                <div className="note-header note-form flex-columns-container">
                    <Avatar entity={user} size={avatarSize} />
                    {form}
                </div>
                <div className="note-form-buttons">
                    <div className="note-form-buttons-left">
                        {toggleHighlight}
                        {addAttachmentsButton}
                        {aiAccessButton}
                    </div>
                    <div className="right">
                        {discardButton}
                        {spinner}
                        {saveButton}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: State): ConnectedProps => ({
    noteAttachments: state.noteAttachments,
    user: state.session.user
});
const mapDispatchToProps: { [action in keyof ConnectedDispatch]: ConnectedDispatch[action] } = {
    addNoteAttachments,
    createNewNote,
    getConfirmation,
    removeNoteAttachments
};
export const NoteEditor = withUsers<OwnProps>(
    connect<ConnectedProps, ConnectedDispatch, OwnProps & ConnectedPropsUsers>(
        mapStateToProps,
        mapDispatchToProps
    )(NoteEditorComponent)
);
